import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Link2, Globe, ChevronUp, ChevronDown, CheckCircle, Copy as CopyIcon } from 'lucide-react';
import { BrandDropdown } from '../common/BrandDropdown';
import type { Brand } from '../../types';
import type { Link } from '../../services/linksService';

interface CreateSmartLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoBack?: () => void;
  onSubmit: (data: { destinationUrl: string; shortCode: string; brandId?: string }) => void;
  brands: Brand[];
  isCreating: boolean;
  initialLinkData?: Link | null;
  isEditMode: boolean;
  prefilledUrl?: string;
}

export const CreateSmartLinkModal: React.FC<CreateSmartLinkModalProps> = ({ 
  isOpen, 
  onClose, 
  onGoBack, 
  onSubmit, 
  brands, 
  isCreating, 
  initialLinkData, 
  isEditMode, 
  prefilledUrl
}) => {
  const { t } = useTranslation();
  
  // destinationUrl state will always hold the BASE URL without UTMs
  const [destinationUrl, setDestinationUrl] = useState(''); 
  const [shortCode, setShortCode] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  const [debouncedShortCode, setDebouncedShortCode] = useState('');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugCheckResult, setSlugCheckResult] = useState<{ available: boolean; message: string; isError?: boolean } | null>(null);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');

  // This state will hold the URL displayed in the input (base + UTMs)
  const [liveUrlToDisplay, setLiveUrlToDisplay] = useState('');
  const [copiedDestinationUrl, setCopiedDestinationUrl] = useState(false);
  
  const generateShortCode = useCallback(() => {
    const randomString = Math.random().toString(36).substring(2, 8);
    setShortCode(randomString);
    setSlugCheckResult(null);
    setDebouncedShortCode(randomString);
  }, []);

  const handleBrandSelect = (brandId: string) => {
    setSelectedBrandId(brandId);
  };

  const parseUtmParams = useCallback((url: string): { baseUrl: string; utms: Record<string, string> } => {
    const params: Record<string, string> = {}; // Ensure params is always initialized
    let parsedBaseUrl = url;

    try {
      const fullUrl = (!url.startsWith('http://') && !url.startsWith('https://') && url.includes('.')) ? `https://${url}` : url;
      const urlObj = new URL(fullUrl);
      
      let pathSegmentForBase = urlObj.pathname;

      if (urlObj.pathname === '/' && !urlObj.search && !urlObj.hash) {
        const hostPart = urlObj.hostname;
        let postHostOriginalInput = '';
        const protoStrippedUrl = fullUrl.replace(/^https?:\/\//, '');
        const hostIndex = protoStrippedUrl.indexOf(hostPart);

        if (hostIndex !== -1) {
            const afterHost = protoStrippedUrl.substring(hostIndex + hostPart.length);
            postHostOriginalInput = afterHost.split('?')[0].split('#')[0];
        }

        if (postHostOriginalInput === '' || postHostOriginalInput === '/') {
          pathSegmentForBase = ''; 
        }
      }
      
      // Start with origin + corrected path + hash
      parsedBaseUrl = `${urlObj.origin}${pathSegmentForBase}${urlObj.hash}`;

      // Now, correctly handle search parameters from the original input URL
      const nonUtmQueryParts = new URLSearchParams();
      urlObj.searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          params[key] = value; // Populate UTM params for return
        } else {
          nonUtmQueryParts.append(key, value); // Collect non-UTM params
        }
      });

      const nonUtmQueryString = nonUtmQueryParts.toString();
      if (nonUtmQueryString) {
        parsedBaseUrl += `?${nonUtmQueryString}`; // Append non-UTM params to the parsedBaseUrl
      }

    } catch (e) {
      console.warn("[parseUtmParams] Error parsing URL:", url, e);
      // Fallback for invalid URLs: try to extract UTMs if there's a query string
      const qIndex = url.indexOf('?');
      if (qIndex !== -1) {
        parsedBaseUrl = url.substring(0, qIndex); // Base is before '?'
        const queryString = url.substring(qIndex + 1);
        try {
          const searchPs = new URLSearchParams(queryString);
          const nonUtmParts = new URLSearchParams();
          searchPs.forEach((value, key) => {
            if (key.startsWith('utm_')) {
              params[key] = value;
            } else {
              nonUtmParts.append(key, value);
            }
          });
          const nonUtmQueryString = nonUtmParts.toString();
          if (nonUtmQueryString) {
            parsedBaseUrl += `?${nonUtmQueryString}`;
          }
        } catch (e2) { /* Query parsing failed, params might be empty */ }
      } else {
        // No query string, the whole URL is considered the base, no UTMs extracted here
        parsedBaseUrl = url;
      }
    }
    return { baseUrl: parsedBaseUrl, utms: params };
  }, []);

  // Effect to build the live URL for display
  useEffect(() => {
    let base = destinationUrl.trim();
    const utmParams = new URLSearchParams();
    if (utmSource) utmParams.append('utm_source', utmSource);
    if (utmMedium) utmParams.append('utm_medium', utmMedium);
    if (utmCampaign) utmParams.append('utm_campaign', utmCampaign);
    if (utmTerm) utmParams.append('utm_term', utmTerm);
    if (utmContent) utmParams.append('utm_content', utmContent);
    const utmQuery = utmParams.toString();

    if (!base && !utmQuery) {
      setLiveUrlToDisplay('');
      return;
    }

    if (!base && utmQuery) {
      setLiveUrlToDisplay(`?${utmQuery}`);
      return;
    }

    try {
      const parseableBase = (base.startsWith('http://') || base.startsWith('https://') || !base.includes('.') || base.startsWith('/'))
        ? base
        : `https://${base}`;

      const urlObj = new URL(parseableBase);
      
      const existingParams = new URLSearchParams(urlObj.search);
      const finalParams = new URLSearchParams();

      existingParams.forEach((value, key) => {
        if (!key.startsWith('utm_')) {
          finalParams.append(key, value);
        }
      });

      utmParams.forEach((value, key) => {
        finalParams.set(key, value);
      });
      
      urlObj.search = finalParams.toString();
      let newDisplayUrl = urlObj.toString();

      if (!base.startsWith('http://') && !base.startsWith('https://') && parseableBase.startsWith('https://') && newDisplayUrl.startsWith('https://')) {
        newDisplayUrl = newDisplayUrl.substring(8);
      }
      
      const originalBasePath = base.split('?')[0].split('#')[0];
      if (urlObj.pathname === '/' && 
          !originalBasePath.endsWith('/') && 
          !urlObj.search && 
          !urlObj.hash &&
          newDisplayUrl.endsWith('/')) {
        newDisplayUrl = newDisplayUrl.substring(0, newDisplayUrl.length - 1);
      }
      
      if (finalParams.toString() === '' && newDisplayUrl.endsWith('?')) {
        newDisplayUrl = newDisplayUrl.substring(0, newDisplayUrl.length - 1);
      }
      setLiveUrlToDisplay(newDisplayUrl.replace(/&$/, ''));

    } catch (error) {
      console.warn("[useEffect liveUrlToDisplay] Error constructing live URL:", error);
      const separator = base.includes('?') ? '&' : '?';
      setLiveUrlToDisplay(utmQuery ? `${base}${separator}${utmQuery}` : base);
    }
  }, [destinationUrl, utmSource, utmMedium, utmCampaign, utmTerm, utmContent]);

  useEffect(() => { // Debounce for shortCode
    const handler = setTimeout(() => {
      setDebouncedShortCode(shortCode.trim());
    }, 750);
    return () => clearTimeout(handler);
  }, [shortCode]);

  useEffect(() => { // Slug availability check
    if (!isOpen) return;
    if (debouncedShortCode) {
      // Slug availability check logic would go here
    } else { 
      setSlugCheckResult(null); 
    }
  }, [debouncedShortCode, isOpen, t]);
  
  useEffect(() => { // Main initialization effect
    if (isOpen) {
      // If a create/update operation is in progress for a *new* link, don't reset form fields.
      // For edit mode, initialLinkData will be present, so this check primarily affects new link creation.
      if (isCreating && !initialLinkData && !isEditMode) {
        return;
      }

      setSlugCheckResult(null);
      setIsCheckingSlug(false);
      setShowAdvancedOptions(false);
      setUtmSource(''); setUtmMedium(''); setUtmCampaign(''); setUtmTerm(''); setUtmContent('');
      setCopiedDestinationUrl(false);

      if (initialLinkData) {
        const { baseUrl, utms } = parseUtmParams(initialLinkData.oldURL);
        setDestinationUrl(baseUrl);
        
        if (Object.keys(utms).length > 0) {
          setUtmSource(utms.utm_source || '');
          setUtmMedium(utms.utm_medium || '');
          setUtmCampaign(utms.utm_campaign || '');
          setUtmTerm(utms.utm_term || '');
          setUtmContent(utms.utm_content || '');
          setShowAdvancedOptions(true);
        }
        
        const newURLParts = initialLinkData.newURL.split('/');
        const originalSlug = newURLParts[newURLParts.length - 1];

        if (isEditMode) {
          setShortCode(originalSlug || ''); // In edit mode, use the exact original slug
          setDebouncedShortCode(originalSlug || '');
        } else { // Duplicating
          const randomSuffix = Math.random().toString(36).substring(2, 9);
          const newShortCodeValue = originalSlug ? `${originalSlug}-copy` : randomSuffix;
          setShortCode(newShortCodeValue);
          setDebouncedShortCode(newShortCodeValue);
        }
        setSelectedBrandId(initialLinkData.brandId || '');
      } else if (prefilledUrl) {
        // Handle prefilled URL case (from UTM modal)
        const { baseUrl, utms } = parseUtmParams(prefilledUrl);
        setDestinationUrl(baseUrl);
        
        if (Object.keys(utms).length > 0) {
          setUtmSource(utms.utm_source || '');
          setUtmMedium(utms.utm_medium || '');
          setUtmCampaign(utms.utm_campaign || '');
          setUtmTerm(utms.utm_term || '');
          setUtmContent(utms.utm_content || '');
          setShowAdvancedOptions(true);
        }
        
        generateShortCode(); // Generate a new short code
      } else {
        // New link being opened (not duplicate or edit)
        setDestinationUrl('');
        generateShortCode(); // This sets shortCode and debouncedShortCode
      }
    }
  }, [isOpen, initialLinkData, isEditMode, isCreating, prefilledUrl, parseUtmParams, generateShortCode]);

  // Separate useEffect for default brand selection (from previous brand flicker fix)
  useEffect(() => {
    // Set default brand only if modal is open for a NEW link, no brand is currently selected, and brands are available.
    if (isOpen && !initialLinkData && !selectedBrandId && brands.length > 0 && brands[0]?.id) {
      setSelectedBrandId(brands[0].id);
    }
  }, [isOpen, initialLinkData, selectedBrandId, brands]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (destinationUrl.trim() === '' || shortCode.trim() === '') return; 
    // For edit mode, slugCheckResult might not be relevant if slug is non-editable
    if (!isEditMode && slugCheckResult && !slugCheckResult.available) {
      alert(t('links.errors.slugTakenOnSubmit'));
      return;
    }
    onSubmit({ destinationUrl: liveUrlToDisplay, shortCode: shortCode.trim(), brandId: selectedBrandId });
  };

  const handleClose = () => {
    setDestinationUrl('');
    setShortCode('');
    setSelectedBrandId('');
    setSlugCheckResult(null);
    setIsCheckingSlug(false);
    setDebouncedShortCode('');
    setShowAdvancedOptions(false);
    setUtmSource(''); setUtmMedium(''); setUtmCampaign(''); setUtmTerm(''); setUtmContent('');
    setLiveUrlToDisplay(''); // Clear the live display URL
    setCopiedDestinationUrl(false);
    onClose();
  };
  
  const handleCopyDestinationUrl = () => {
    if (liveUrlToDisplay) {
      navigator.clipboard.writeText(liveUrlToDisplay);
      setCopiedDestinationUrl(true);
      setTimeout(() => setCopiedDestinationUrl(false), 2000);
    }
  };
  
  if (!isOpen) return null;

  const modalTitle = isEditMode ? t('links.editSmartLink', 'Edit Smart Link') : t('links.createSmartLink', 'Create a smart link');
  const submitButtonText = isEditMode 
    ? (isCreating ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')) 
    : (isCreating ? t('common.creating', 'Creating...') : t('links.createLinkButton', 'Create Link'));

  const canSubmit = destinationUrl.trim() !== '' && 
                    shortCode.trim() !== '' && 
                    !isCreating && // Now correctly reflects overall processing state
                    (isEditMode || isCheckingSlug === false) && 
                    (isEditMode || slugCheckResult === null || slugCheckResult.available);

  const shouldApplyUtmStyling = utmSource || utmMedium || utmCampaign || utmTerm || utmContent;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" /> 
        
        <div
          className="relative bg-white rounded-xl border-2 border-black shadow-xl w-full max-w-lg p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-900">
              <Link2 className="w-5 h-5 text-primary-600" />
              {modalTitle}
            </h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Destination URL */}
            <div>
              <label htmlFor="destination-url-input" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Globe className="w-4 h-4 mr-1.5 text-gray-400" />
                {t('links.destinationUrlLabel', 'Destination URL')}
              </label>
              <div className="relative">
                <textarea
                  id="destination-url-input"
                  value={liveUrlToDisplay}
                  onChange={(e) => {
                    const { baseUrl, utms } = parseUtmParams(e.target.value);
                    setDestinationUrl(baseUrl);
                    setUtmSource(utms.utm_source || '');
                    setUtmMedium(utms.utm_medium || '');
                    setUtmCampaign(utms.utm_campaign || '');
                    setUtmTerm(utms.utm_term || '');
                    setUtmContent(utms.utm_content || '');
                  }}
                  placeholder={t('links.destinationUrlPlaceholder', 'https://www.socialness.ai/features/ai-link-builder')}
                  className={`w-full text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm disabled:opacity-50 disabled:cursor-not-allowed border placeholder-gray-400 resize-none overflow-hidden ${ // Base styling
                    shouldApplyUtmStyling 
                      ? 'bg-primary-50 !border-primary-600 text-gray-700' // UTM active specific styling (border, bg)
                      : 'border-gray-300' // Default border
                  } ${ // Padding logic - default p-2.5 seems to match the screenshot better for the purple box.
                    liveUrlToDisplay ? 'p-2.5 pr-10' : 'p-2.5' // Apply base padding, add pr-10 if copy button visible
                  }`}
                  rows={shouldApplyUtmStyling ? 4 : 1}
                  required
                  disabled={isCreating} // isCreating now correctly reflects processing state for both modes
                />
                {/* Show copy button if there's a URL to copy */}
                {liveUrlToDisplay && (
                    <button
                        type="button"
                        onClick={handleCopyDestinationUrl}
                        className="absolute top-1/2 right-2 transform -translate-y-1/2 p-1 text-primary-600 hover:text-primary-800 focus:outline-none"
                        title={copiedDestinationUrl ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
                    >
                        {copiedDestinationUrl ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                        <CopyIcon className="w-5 h-5" />
                        )}
                    </button>
                )}
              </div>
            </div>

            {/* Short Link */}
            <div>
              <label htmlFor="short-code-input" className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.shortLinkLabel', 'Short Link')}
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 h-[38px] border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md">
                  go.socialness.ai/
                </span>
                <input
                  type="text"
                  id="short-code-input"
                  value={shortCode}
                  onChange={(e) => {
                    setShortCode(e.target.value);
                    if (slugCheckResult) setSlugCheckResult(null);
                  }}
                  placeholder={t('links.shortCodePlaceholder', 'custom-slug')} 
                  className="flex-1 block w-full px-3 h-[38px] border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                  disabled={isCreating || isEditMode} // Disable if processing OR if in edit mode (shortcode non-editable)
                  minLength={3}
                />
                <button
                  type="button"
                  onClick={generateShortCode}
                  disabled={isCreating || isEditMode} // Disable if processing OR if in edit mode (shortcode non-editable)
                  className="inline-flex items-center px-3 h-[38px] border border-l-0 border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {t('links.generateButton', 'Generate')}
                </button>
              </div>
              {/* Slug Availability Message - Hide in edit mode */}
              {!isEditMode && (isCheckingSlug || slugCheckResult) && (
                <div className="text-xs px-1 mt-1">
                  {isCheckingSlug && <p className="text-gray-500">{t('links.checkingSlug', 'Checking availability...')}</p>}
                  {slugCheckResult && !isCheckingSlug && (
                    <p className={`${slugCheckResult.isError || !slugCheckResult.available ? 'text-red-600' : 'text-green-600'}`}>
                      {slugCheckResult.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Brand Dropdown */}
            <div>
              <label htmlFor="brand-select-modal" className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.brandLabel', 'Brand')}
              </label>
              <BrandDropdown
                brands={brands}
                selectedBrandId={selectedBrandId}
                onBrandSelect={handleBrandSelect}
                isLoading={isCreating} // isCreating now correctly reflects processing state for both modes
                className="h-[38px]"
              />
            </div>
            
            {/* Advanced Options / UTM Parameters Toggle */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>{t('links.advancedOptions', 'Advanced Options')}</span>
                {showAdvancedOptions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            </div>

            {/* UTM Parameters Section */}
            {showAdvancedOptions && (
              <div className="pt-3">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('links.utmParameters', 'UTM Parameters:')}
                </label>
                <div className="p-3 border border-gray-300 rounded-md bg-gray-50 space-y-2">
                  {/* utm_source */}
                  <div className="flex items-center">
                    <label 
                      htmlFor="utm-source"
                      className="w-1/3 text-xs text-gray-600 truncate pr-2"
                    >
                      {t('links.source', 'Source (utm_source)')}:
                    </label>
                    <input 
                      type="text" id="utm-source" value={utmSource} onChange={(e) => setUtmSource(e.target.value)}
                      placeholder="e.g., newsletter, google"
                      className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
                      disabled={isCreating} // isCreating now correctly reflects processing state
                    />
                  </div>
                  {/* utm_medium */}
                  <div className="flex items-center">
                    <label htmlFor="utm-medium" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.medium', 'Medium (utm_medium)')}:</label>
                    <input type="text" id="utm-medium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="e.g., email, cpc" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_campaign */}
                  <div className="flex items-center">
                    <label htmlFor="utm-campaign" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.campaign', 'Campaign (utm_campaign)')}:</label>
                    <input type="text" id="utm-campaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="e.g., summer_sale" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_term */}
                  <div className="flex items-center">
                    <label htmlFor="utm-term" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.term', 'Term (utm_term)')}:</label>
                    <input type="text" id="utm-term" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="e.g., running+shoes" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_content */}
                  <div className="flex items-center">
                    <label htmlFor="utm-content" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.content', 'Content (utm_content)')}:</label>
                    <input type="text" id="utm-content" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="e.g., logolink, textlink" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Buttons - Footer of the modal content */}
            <div className="flex justify-end items-center gap-3 pt-5 border-t border-gray-200 mt-1">
              <button
                type="button"
                onClick={onGoBack || handleClose}
                disabled={isCreating} // isCreating now correctly reflects processing state for both modes
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {onGoBack ? t('common.goBack', 'Go Back') : t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={!canSubmit} // canSubmit logic now considers isEditMode
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCreating ? ( 
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {/* submitButtonText already handles Creating.../Saving... based on isCreating and isEditMode */}
                    {isEditMode ? t('common.saving', 'Saving...') : t('common.creating', 'Creating...')}
                  </>
                ) : (
                  submitButtonText // This will show "Create Link" or "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 